import { useState } from "react"
import { format } from "date-fns"
import { FaFileAlt } from "react-icons/fa"
import { Button } from "@/modules/shared/ui/components/button"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import {
  DataTable,
  TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { FolderTreeBrowser } from "../components/FolderTreeBrowser"
import { MoveDocumentModal } from "../components/MoveDocumentModal"
import { PermissionPanel } from "../components/PermissionPanel"
import {
  useAllDocuments,
  useDeleteDocument,
  useRestoreDocument,
} from "../../hooks/useDocuments"
import { DocumentDto } from "../../entities/DocumentDto"

type PanelTarget = { type: "document" | "folder"; id: string; name: string }

export default function AdminDocumentsPage() {
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(
    undefined,
  )
  const [panelTarget, setPanelTarget] = useState<PanelTarget | null>(null)
  const [moveTarget, setMoveTarget] = useState<DocumentDto | null>(null)

  const { data: documents = [], isLoading } = useAllDocuments(selectedFolderId)
  const deleteDocument = useDeleteDocument()
  const restoreDocument = useRestoreDocument()

  const columns: TableColumn<DocumentDto>[] = [
    {
      key: "fileName",
      label: "File Name",
      render: (doc) => (
        <div className="flex items-center gap-2">
          <FaFileAlt className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium truncate max-w-xs">{doc.fileName}</span>
        </div>
      ),
    },
    {
      key: "createdByName",
      label: "Owner",
      render: (doc) => (
        <span className="text-muted-foreground">
          {doc.createdByName ?? doc.createdBy}
        </span>
      ),
    },
    {
      key: "updatedAt",
      label: "Last Updated",
      render: (doc) => (
        <span className="text-muted-foreground">
          {format(new Date(doc.updatedAt), "MMM d, yyyy")}
        </span>
      ),
    },
    {
      key: "id",
      label: "",
      render: (doc) => (
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMoveTarget(doc)}
          >
            Move to folder
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setPanelTarget({
                type: "document",
                id: doc.id,
                name: doc.fileName,
              })
            }
          >
            Manage Permissions
          </Button>
          {doc.isDeleted ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => restoreDocument.mutate(doc.id)}
              disabled={restoreDocument.isPending}
            >
              Restore
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => deleteDocument.mutate(doc.id)}
              disabled={deleteDocument.isPending}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="flex h-full gap-0">
      {/* Folder tree sidebar */}
      <aside className="w-56 shrink-0 border-r bg-muted/20 p-3 overflow-y-auto">
        <FolderTreeBrowser
          selectedFolderId={selectedFolderId}
          onSelectFolder={setSelectedFolderId}
          onManageFolderPermissions={(id, name) =>
            setPanelTarget({ type: "folder", id, name })
          }
        />
      </aside>

      {/* Main content */}
      <main className="flex-1 p-6 overflow-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">All Documents</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Organisation-wide document management
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <Skeleton key={n} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
            <DataTable columns={columns} data={documents} />
          </div>
        )}
      </main>

      {/* Move to folder modal */}
      {moveTarget && (
        <MoveDocumentModal
          document={moveTarget}
          onClose={() => setMoveTarget(null)}
        />
      )}

      {/* Permission slide-over */}
      {panelTarget && (
        <PermissionPanel
          open={!!panelTarget}
          onClose={() => setPanelTarget(null)}
          type={panelTarget.type}
          targetId={panelTarget.id}
          targetName={panelTarget.name}
        />
      )}
    </div>
  )
}
